/**
 * The sidebar template (after shadcn/ui's Sidebar: SidebarProvider → Sidebar / Header / Content / Group / Menu /
 * Footer / Rail / Trigger), in this app's CSS: both rails (the dashboard's and the admin dashboard's, nav/Sidebar.tsx)
 * are built from these parts, so they fold, open and look the same.
 *
 *   SidebarFrame    the <nav>: header on top, groups in the middle, footer at the bottom; `collapsed` folds it to its
 *                   icons (data-collapsed; the Shell sets --rail-width through data-rail on .app) and the edge strip
 *                   (SidebarEdge, like shadcn's SidebarRail) toggles it on click.
 *   SidebarGroup    a section with an optional uppercase label (hidden when folded).
 *   SidebarMenuButton  one row: icon + label (+ badge); a link or a button; `data-label` is the tooltip when folded.
 *   SidebarSearch   type to search: the first keystroke opens the ⌘K palette with it (search/CommandPalette.tsx); folded,
 *                   an icon button.
 *   useSidebarShortcut  ⌘B / Ctrl+B toggles the sidebar (shadcn's default), never while typing in a field or editor.
 */
import { useEffect, useState, type MouseEvent, type ReactNode } from "react";
import { Search } from "lucide-react";
import { navigate, plainClick } from "../app/router";

/** Plain clicks navigate in place; Cmd/Ctrl/Shift-click opens a new tab or window as usual. */
export const go = (path: string) => (e: MouseEvent) => { if (!plainClick(e)) return; e.preventDefault(); navigate(path); };

export function SidebarFrame({ label, collapsed = false, admin = false, onToggle, header, footer, children }: {
  label: string; collapsed?: boolean; admin?: boolean;
  /** Fold / unfold (the edge strip and the header's toggle call it). */
  onToggle?: () => void;
  header?: ReactNode; footer?: ReactNode; children?: ReactNode;
}) {
  return (
    <nav className={admin ? "rail rail--admin" : "rail"} aria-label={label} data-collapsed={collapsed || undefined} data-state={collapsed ? "collapsed" : "expanded"}>
      <div className="rail-top">
        {header}
        {children}
      </div>
      {footer && <div className="rail-bottom">{footer}</div>}
      {onToggle && <SidebarEdge collapsed={collapsed} onToggle={onToggle} />}
    </nav>
  );
}

/** The strip along the sidebar's edge: click to fold or unfold (shadcn's SidebarRail). Mouse only; the header's toggle is the keyboard's. */
export function SidebarEdge({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  return <button type="button" className="rail__edge" tabIndex={-1} aria-hidden title={collapsed ? "Expand the sidebar" : "Collapse the sidebar"} onClick={onToggle} />;
}

export function SidebarGroup({ label, className, children, ...rest }: { label?: ReactNode; className?: string; children?: ReactNode; "aria-label"?: string }) {
  return (
    <section className={className ? `rail-group ${className}` : "rail-group"} {...rest}>
      {label && <h3 className="nav-section-label">{label}</h3>}
      {children}
    </section>
  );
}

export function SidebarMenuButton({ href, onClick, icon, label, active = false, badge, className, ...rest }: {
  href?: string; onClick?: () => void; icon: ReactNode; label: string; active?: boolean; badge?: ReactNode; className?: string;
  "aria-haspopup"?: "dialog" | "menu"; "aria-expanded"?: boolean;
}) {
  const cls = className ? `nav-item ${className}` : "nav-item";
  const body = <>{icon}<span className="nav-label">{label}</span>{badge}</>;
  if (href) return <a className={cls} href={href} data-label={label} aria-current={active ? "page" : undefined} onClick={(e) => { onClick?.(); go(href)(e); }} {...rest}>{body}</a>;
  return <button type="button" className={cls} data-label={label} onClick={onClick} {...rest}>{body}</button>;
}

const openPalette = (q = "") => dispatchEvent(new CustomEvent("palette:open", { detail: { mode: "search", q } }));

/** Type to search: whatever you type goes straight into the ⌘K palette, which takes over (↑↓, ↵, scopes). */
export function SidebarSearch({ collapsed = false }: { collapsed?: boolean }) {
  const [v, setV] = useState("");
  if (collapsed) {
    return <button type="button" className="rail__search rail__search--icon" aria-label="Search" title="Search ⌘K" aria-keyshortcuts="Meta+K Control+K" onClick={() => openPalette()}><Search size={14} aria-hidden /></button>;
  }
  return (
    <label className="rail__search">
      <Search size={14} aria-hidden />
      <input value={v} placeholder="Search" aria-label="Search meetings, bills, people, organizations" aria-keyshortcuts="Meta+K Control+K" autoComplete="off" spellCheck={false}
        onChange={(e) => { const q = e.target.value; setV(""); if (q.trim()) openPalette(q); }}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); openPalette(v); } }}
        onClick={() => openPalette()} />
      <kbd>⌘K</kbd>
    </label>
  );
}

/** ⌘B / Ctrl+B folds or unfolds the sidebar, unless the keystroke belongs to a field or the editor (bold). */
export function useSidebarShortcut(toggle: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey || e.key.toLowerCase() !== "b") return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      e.preventDefault();
      toggle();
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [toggle]);
}
